/**
 * Personal thread categories — the viewer's own section list (chat-thread
 * experience PR-5, 2026-09-09; built-ins first-class 2026-09-09). ONE doc:
 * `users/{uid}/chat_threads_meta/categories`
 * `{ items: [{ id, name, order }], collapsed: string[], builtinOrderTouched?,
 * updatedAt }`, owner-readable/writable under the existing `chat_threads_meta`
 * rule (no rules change). `builtinOrderTouched` is written ONLY by a real
 * reorder (useThreadCategories.moveCategory): until then the built-ins render
 * in the platform default order whatever order a rename or a collapse
 * happened to store (types/threadCategories.orderBuiltinsByDefault). A thread joins a section through `category` on the viewer's
 * index doc (chatThreadIndexRepository, via the thread store).
 */
import { onSnapshot, serverTimestamp, setDoc, type Unsubscribe } from 'firebase/firestore';
import { Paths, assertCurrentUid, withBackoff } from './firestoreUtils';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import {
  normalizeThreadCategories,
  parseThreadCategories,
  type ThreadCategory,
} from '../types/threadCategories';

const META_DOMAIN = 'chat_threads_meta';
const CATEGORIES_DOC_ID = 'categories';
const COLLAPSED_MAX = 64;

export interface ThreadCategoriesState {
  /** The stored list, normalized (custom + any stored built-ins); see withBuiltinSections. */
  items: ThreadCategory[];
  /** Section ids the viewer collapsed. */
  collapsed: string[];
  /** Stored custom ids that fold into a built-in (see parseThreadCategories). */
  foldAliases: Record<string, string>;
  /** True once the viewer has reordered sections themselves (their stored order then wins over the default). */
  builtinOrderTouched: boolean;
}

export interface SaveThreadCategoriesOptions {
  /** Stamp the doc as reordered by the viewer (a real move — never a rename or a collapse). */
  builtinOrderTouched?: boolean;
}

function categoriesRef(uid: string) {
  return Paths.domainDoc(uid, META_DOMAIN, CATEGORIES_DOC_ID);
}

export function normalizeCollapsed(raw: unknown): string[] {
  const out: string[] = [];
  for (const entry of Array.isArray(raw) ? raw : []) {
    const id = typeof entry === 'string' ? entry.trim() : '';
    if (id && !out.includes(id)) out.push(id);
    if (out.length >= COLLAPSED_MAX) break;
  }
  return out;
}

/**
 * Live state of the viewer's sections. Fires an empty state for a missing
 * doc; errors (offline, rules) are swallowed — the rail keeps its last list.
 */
export function subscribeThreadCategories(
  uid: string,
  onChange: (state: ThreadCategoriesState) => void,
): Unsubscribe {
  assertCurrentUid(uid, 'subscribeThreadCategories');
  return onSnapshot(
    categoriesRef(uid),
    (snap) => {
      const data = snap.exists()
        ? (snap.data() as { items?: unknown; collapsed?: unknown; builtinOrderTouched?: unknown })
        : null;
      const parsed = parseThreadCategories(data?.items);
      onChange({
        items: parsed.items,
        collapsed: normalizeCollapsed(data?.collapsed),
        foldAliases: parsed.foldAliases,
        builtinOrderTouched: data?.builtinOrderTouched === true,
      });
    },
    () => { /* keep the last-known list */ },
  );
}

/** Replace the viewer's section list (normalized before it is written) and, when given, the collapsed set. */
export async function saveThreadCategories(
  uid: string,
  items: readonly ThreadCategory[],
  collapsed?: readonly string[],
  opts?: SaveThreadCategoriesOptions,
): Promise<ThreadCategory[]> {
  addUserScopedWriteGuard(uid, 'saveThreadCategories');
  assertCurrentUid(uid, 'saveThreadCategories');
  const normalized = normalizeThreadCategories(items);
  await withBackoff(() => setDoc(
    categoriesRef(uid),
    {
      items: normalized,
      ...(collapsed ? { collapsed: normalizeCollapsed(collapsed) } : {}),
      ...(opts?.builtinOrderTouched ? { builtinOrderTouched: true } : {}),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  ));
  return normalized;
}
